import React, { useEffect } from 'react';
import {Bar} from 'react-chartjs-2';
import ApiService from '../../services/apiService';
import { useOktaAuth } from '@okta/okta-react';
import { Api } from '../../types/apiData';
import { count } from 'console';
import { main } from '@popperjs/core';
import { useNavigate } from 'react-router-dom';
import { useDarkTheme } from '../../contexts/DarkThemeContext';
import { CHART_COLOR_PALETTE, CHART_COLOR_PALETTE_DARK } from './shared/chartColors';

const ApiByProduct: React.FC = () => {
    const [productsCounts, setProductsCounts] = React.useState<Map<string, number>>(new Map());
    const { authState } = useOktaAuth();
    const navigate = useNavigate();
    const { isDarkTheme } = useDarkTheme();
    const [apiData, setApiData] = React.useState<any[]>([]);
    const [chartData, setChartData] = React.useState<number[]>([]);
    const [chartLabels, setChartLabels] = React.useState<string[]>([]);

    const handleChartClick = (event: any, elements: any[]) => {
        if (elements.length > 0) {
            const clickedIndex = elements[0].index;
            const clickedLabel = chartLabels[clickedIndex];
            console.log('Chart clicked:', clickedLabel);
            
            // Create URL parameters based on the clicked product
            const params = new URLSearchParams();
            
            // Find the area hierarchy that matches this product
            const matchingApi = apiData.find(api => {
                const productName = api.areas && api.areas.find((area: any) => area.level === 2)?.name;
                return productName === clickedLabel;
            });
            
            if (matchingApi && matchingApi.areas) {
                const productArea = matchingApi.areas.find((area: any) => area.level === 2);
                if (productArea) {
                    params.set('areaId', productArea.id.toString());
                }
            }
            
            // Navigate to table view with filters
            navigate(`/table-view?${params.toString()}`);
        }
    };

    const extractData = (apiData: any[]) => {
        console.log("Extracting data from:", apiData);
        
        //Add a property name and return the processed data
        const processedData = apiData.map((api) => {
            const products = api.areas && api.areas.find((area: any) => area.level === 2)?.name || '';
            //console.log(`API ID: ${api.id || 'unknown'}, Found product type: ${products}`);
            return { products };
        });
        
        console.log("Extraction complete, processed data:", processedData);
        return processedData;
    }

    function countBusinessUnit<B>(array: B[], keyExtractor: (item: B) => string): Map<string, number> {
        const counts = new Map<string, number>();
        array.forEach(item => {
            const key = keyExtractor(item);
            if (key) { // Only count if key exists
                // Increment the count for the key
                counts.set(key, (counts.get(key) || 0) + 1);
            }
        });
        
        return counts;
    }

    const processMapData = (countsMap: Map<string, number>) => {
        console.log("Processing map data for chart:", countsMap);
        const labels: string[] = [];
        const values: number[] = [];
        
        // Convert Map entries to arrays for the chart
        countsMap.forEach((count, businessUnit) => {
            if (businessUnit) { // Only include non-empty business units
                labels.push(businessUnit);
                values.push(count);
            }
        });
        
        setChartLabels(labels);
        setChartData(values);
        console.log("Chart data prepared:", { labels, values });
    }

    const getData = async() => {
        try {
            const apiService = new ApiService();
            const data = (await apiService.getAllApis(authState)) as any[];
            console.log("Raw API data fetched:", data);
            setApiData(data);
            
            const processed = extractData(data);
            console.log("Processed API data: ", processed);
            const counts = countBusinessUnit(processed, (item) => item.products);
            console.log("Visibility Counts: ", counts);
            setProductsCounts(counts);
            processMapData(counts);
        } catch (error) {
            console.error("Error fetching API data for the Graphics:", error);
        } 
    }

    useEffect(() => {
        getData();
    }, [authState]);

    useEffect(() => {
        if (productsCounts.size > 0) {
            processMapData(productsCounts);
        }
    }, [setProductsCounts]);

    const textColor = isDarkTheme ? 'rgba(255, 255, 255, 0.85)' : undefined;
    const tickColor = isDarkTheme ? 'rgba(255, 255, 255, 0.65)' : undefined;
    const gridColor = isDarkTheme ? 'rgba(255, 255, 255, 0.08)' : undefined;

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        onClick: handleChartClick,
        onHover: (event: any, elements: any[]) => {
            event.native.target.style.cursor = elements.length > 0 ? 'pointer' : 'default';
        },
        plugins: {
            title: {
                display: true,
                text: 'API by Products',
                color: textColor,
                font: {
                    size: 20,
                },
                padding: {
                    top: 10,
                    bottom: 20,
                },
            },
            legend: {
                display: false,
            },
        },
        scales: {
            x: {
                ticks: {
                    color: tickColor,
                    font: {
                        size: 10,
                    }
                },
                grid: {
                    color: gridColor,
                }
            },
            y: {
                ticks: {
                    color: tickColor,
                    font: {
                        size: 10,
                    },
                    precision: 0,
                },
                grid: {
                    color: gridColor,
                },
                beginAtZero: true,
            }
        }
    }

    const labels = chartLabels;
    
    const data = {
        labels,
        datasets: [
            {
                label: '', // Empty string instead of 'API Count'
                data: chartData,
                backgroundColor: isDarkTheme
                    ? CHART_COLOR_PALETTE_DARK.BACKGROUND.slice(0, 8)
                    : CHART_COLOR_PALETTE.BACKGROUND.slice(0, 8),
                borderColor: isDarkTheme
                    ? CHART_COLOR_PALETTE_DARK.BORDER.slice(0, 8)
                    : CHART_COLOR_PALETTE.BORDER.slice(0, 8),
                borderWidth: 1,
            },
        ],
    }

    return (
        <div className="graphic-content">
            <Bar options={options} data={data} />
        </div>
    )
}

export default ApiByProduct;